import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Star, Loader2, CheckCircle2, ChevronLeft, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function ReviewPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [verifyError, setVerifyError] = useState(null);
  const [order, setOrder] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) {
      setVerifyError("Missing review token. Please use the link from your email.");
      setLoading(false);
      return;
    }
    api
      .get("/reviews/verify-token", { params: { token } })
      .then((r) => {
        setOrder(r.data);
        setName(r.data.customer_name || "");
        setAlreadySubmitted(!!r.data.already_submitted);
      })
      .catch((e) => {
        setVerifyError(e?.response?.data?.detail || "This review link is invalid or expired.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const canSubmit = useMemo(
    () => name.trim().length >= 1 && text.trim().length >= 10 && rating >= 1 && !submitting,
    [name, text, rating, submitting]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await api.post("/reviews/submit", {
        token,
        name: name.trim(),
        rating,
        text: text.trim(),
      });
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Verifying your link…
        </div>
      </Wrapper>
    );
  }

  if (verifyError) {
    return (
      <Wrapper>
        <h1 className="font-display text-3xl text-[#0A192F] mb-3">Review link unavailable</h1>
        <p className="text-slate-600 mb-6">{verifyError}</p>
        <Link to="/" className="text-[#C5A059] font-accent text-xs uppercase tracking-widest">
          ← Back to AIGL Channel
        </Link>
      </Wrapper>
    );
  }

  if (result?.ok) {
    return <SuccessView result={result} order={order} />;
  }

  if (alreadySubmitted) {
    return (
      <Wrapper>
        <h1 className="font-display text-3xl text-[#0A192F] mb-3">Thanks — you've already shared a review</h1>
        <p className="text-slate-600 mb-6">
          Our team reviews submissions within 48 hours. Once approved, your words will appear on the AIGL homepage.
        </p>
        <Link to="/" className="text-[#C5A059] font-accent text-xs uppercase tracking-widest">
          ← Back to AIGL Channel
        </Link>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center text-slate-400 hover:text-[#0A192F] text-xs font-accent tracking-widest uppercase"
          data-testid="review-back-link"
        >
          <ChevronLeft className="w-3 h-3 mr-1" /> Back
        </Link>
      </div>

      <span className="font-accent text-[#C5A059] text-[11px] tracking-[3px]">
        VERIFIED BUYER REVIEW
      </span>
      <h1 className="font-display text-4xl text-[#0A192F] mt-3 mb-2 leading-tight">
        How was your <span className="italic">{order?.product_name}?</span>
      </h1>
      <p className="text-slate-600 text-sm mb-8 max-w-lg">
        Your honest take helps the next founder decide if AIGL is right for them. Takes 30 seconds.
        Submit and we'll send you a <b>15% discount</b> code for your next plan.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6" data-testid="review-form">
        <Field label="Your rating">
          <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onClick={() => setRating(n)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`review-star-${n}`}
                aria-label={`${n} stars`}
              >
                <Star
                  className={`w-8 h-8 transition ${
                    (hover || rating) >= n
                      ? "fill-[#C5A059] text-[#C5A059]"
                      : "text-slate-300"
                  }`}
                />
              </button>
            ))}
            <span className="ml-3 text-sm text-slate-500">{rating} of 5</span>
          </div>
        </Field>

        <Field label="Your name">
          <input
            data-testid="review-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            className="w-full border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:border-[#C5A059]"
            placeholder="e.g., Marcus Bennett"
            required
          />
        </Field>

        <Field label={`Your review (${text.trim().length}/600)`}>
          <textarea
            data-testid="review-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={600}
            rows={6}
            placeholder="What worked? What surprised you? How is the plan helping you build?"
            className="w-full border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:border-[#C5A059] resize-none"
            required
          />
          {text.trim().length > 0 && text.trim().length < 10 && (
            <div className="text-xs text-amber-700 mt-1">At least 10 characters, please.</div>
          )}
        </Field>

        <button
          data-testid="review-submit-btn"
          type="submit"
          disabled={!canSubmit}
          className="w-full bg-[#0A192F] hover:bg-[#0A192F]/90 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-accent text-xs tracking-[2px] py-4 transition"
        >
          {submitting ? (
            <span className="inline-flex items-center"><Loader2 className="w-4 h-4 animate-spin mr-2" /> SUBMITTING…</span>
          ) : (
            "SUBMIT REVIEW & GET MY COUPON"
          )}
        </button>

        <p className="text-[11px] text-slate-400 text-center">
          We moderate every review before it goes live. Your email stays private.
        </p>
      </form>
    </Wrapper>
  );
}

const Wrapper = ({ children }) => (
  <div className="min-h-screen bg-[#F8FAFC] py-16 px-5">
    <div className="max-w-xl mx-auto bg-white border border-slate-200 p-8 sm:p-12" data-testid="review-page">
      {children}
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[11px] font-accent tracking-[2px] text-slate-500 uppercase mb-2">
      {label}
    </label>
    {children}
  </div>
);

const SuccessView = ({ result, order }) => {
  const copyCode = () => {
    if (result.coupon_code) {
      navigator.clipboard.writeText(result.coupon_code);
      toast.success("Coupon code copied to clipboard");
    }
  };
  return (
    <Wrapper>
      <div className="text-center" data-testid="review-success">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-5" />
        <h1 className="font-display text-3xl text-[#0A192F] mb-3">
          Thank you — you just helped the next founder.
        </h1>
        <p className="text-slate-600 mb-8">
          Your review for <b>{order?.product_name}</b> is in moderation. Once approved (usually within 48 hours)
          you'll see it on the AIGL homepage.
        </p>
        {result.coupon_code && (
          <div className="bg-[#FBF8F1] border-2 border-dashed border-[#C5A059] p-6 mb-8">
            <div className="text-[10px] font-accent tracking-[3px] text-[#C5A059] mb-2">
              YOUR THANK-YOU COUPON
            </div>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <div
                className="bg-white border border-[#C5A059] font-mono font-bold text-lg sm:text-xl text-[#0A192F] px-4 py-2 tracking-widest"
                data-testid="review-coupon-code"
              >
                {result.coupon_code}
              </div>
              <button
                type="button"
                onClick={copyCode}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0A192F] border border-slate-300 px-3 py-2"
                data-testid="copy-coupon-btn"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <div className="text-[13px] text-[#0F172A] mt-3">
              <b>{result.coupon_discount_pct || 15}% off</b> your next AIGL plan
            </div>
          </div>
        )}
        <Link
          to="/"
          className="inline-block bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] font-accent text-xs tracking-[2px] px-6 py-3 transition"
          data-testid="back-to-store-btn"
        >
          BROWSE PLANS &rarr;
        </Link>
      </div>
    </Wrapper>
  );
};
